import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icons';
import { useVisualViewport } from '@/lib/useVisualViewport';
import { useScrollLock } from '@/lib/useScrollLock';
import { useKeepFieldInView } from '@/lib/useKeepFieldInView';

/*
 * The reference's sheet: slides in from the right on desktop, rises from the bottom on phones (as a fade —
 * motion on phones is a fade, not a slide). `wide` = 60% of the screen (the unit-condition report).
 * Radix Dialog underneath: focus trapped inside, Escape closes, focus returns to the opener.
 *
 * 1.8.4 §7 (D-1.8.4-7) — typing on an iPhone or an iPad, one way for every sheet (the Ask sheet's way since 1.5.17):
 *   - the sheet ends where the keyboard begins (useVisualViewport keeps --vv-height / --kb-inset / data-kb="open" on
 *     it; src/styles/kb184.css lays it out above the keyboard), so its own content scrolls to the last field and its
 *     buttons (the footer) stay on screen with the keyboard up;
 *   - the box being typed in is brought into view inside the sheet, once, on its own focus (useKeepFieldInView —
 *     never from a viewport event);
 *   - the page behind stays still on a phone or a touch screen (useScrollLock, the 1.5.17 / 1.5.19 lock).
 */
export const Sheet = SheetPrimitive.Root;
export const SheetTrigger = SheetPrimitive.Trigger;
export const SheetClose = SheetPrimitive.Close;

export interface SheetContentProps extends Omit<React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>, 'title'> {
  title: React.ReactNode;
  wide?: boolean;
  footer?: React.ReactNode;
  /** The control that opened the sheet when it was opened from state (not a SheetTrigger): focus returns there. */
  returnFocusTo?: React.RefObject<HTMLElement | null>;
}

/** Where the page behind is pinned while a sheet is open: a phone-sized screen, or any touch screen (an iPad). */
const PINS_PAGE = () => typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 900px), (pointer: coarse)').matches;

export const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  ({ className, children, title, wide, footer, returnFocusTo, onCloseAutoFocus, ...props }, ref) => {
    const { t } = useTranslation();
    const [el, setEl] = React.useState<HTMLDivElement | null>(null);
    const body = React.useRef<HTMLDivElement>(null);
    const own = React.useCallback(
      (node: HTMLDivElement | null) => {
        setEl(node);
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );
    // `el` is the open sheet (null while it is closed: Radix keeps this component mounted and unmounts only its content)
    useVisualViewport(el);
    useScrollLock(!!el && PINS_PAGE());
    useKeepFieldInView(el, body);
    return (
      <SheetPrimitive.Portal>
        <SheetPrimitive.Overlay className="backdrop" />
        <SheetPrimitive.Content ref={own} className={cn('sheet', wide && 'wide', className)} aria-describedby={undefined}
          onCloseAutoFocus={(e) => {
            onCloseAutoFocus?.(e);
            if (returnFocusTo?.current && !e.defaultPrevented) {
              e.preventDefault();
              returnFocusTo.current.focus();
            }
          }}
          {...props}
        >
          <div className="sheet-h">
            <SheetPrimitive.Title asChild>
              <h2>{title}</h2>
            </SheetPrimitive.Title>
            <SheetPrimitive.Close className="iconbtn" aria-label={t('app.close')}>
              <Icon name="close" />
            </SheetPrimitive.Close>
          </div>
          <div className="sheet-b" ref={body}>
            {children}
          </div>
          {footer ? <div className="sheet-f">{footer}</div> : null}
        </SheetPrimitive.Content>
      </SheetPrimitive.Portal>
    );
  },
);
SheetContent.displayName = 'SheetContent';
